import { useState, useEffect, useRef, useCallback } from "react";

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://");

export default function useWebSocket(user) {
  const [lastMessage, setLastMessage] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const messageQueue = useRef([]);

  const connect = useCallback(() => {
    if (!user?.user_id) return;

    const token = localStorage.getItem("token");
    if (!token) return;

    // Close existing connection
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      const ws = new WebSocket(`${WS_URL}/ws/${token}`);

      ws.onopen = () => {
        setIsConnected(true);
        console.log("WebSocket connected");

        // Process queued messages
        while (messageQueue.current.length > 0) {
          const msg = messageQueue.current.shift();
          ws.send(JSON.stringify(msg));
        }

        // Ping every 25 seconds to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send("ping");
          }
        }, 25000);
      };

      ws.onmessage = (event) => {
        if (event.data === "pong") return;

        try {
          const data = JSON.parse(event.data);
          // Immediate state update for real-time feel
          setLastMessage(data);

          if (data.type === "notification") {
            setNotifications(prev => [...prev, data.data]);
          }
        } catch (e) {
          console.error("WebSocket message parse error:", e);
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        console.log("WebSocket disconnected", event.code);

        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }

        // Reconnect faster - 1 second
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 1000);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      wsRef.current = ws;
    } catch (error) {
      console.error("WebSocket connection error:", error);
      // Retry connection
      reconnectTimeoutRef.current = setTimeout(connect, 2000);
    }
  }, [user?.user_id]);

  useEffect(() => {
    connect();

    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000);
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [connect]);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Send message through WebSocket (for future use)
  const sendMessage = useCallback((data) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    } else {
      messageQueue.current.push(data);
    }
  }, []);

  return {
    lastMessage,
    notifications,
    isConnected,
    clearNotifications,
    sendMessage
  };
}
